import { useRef } from "react";
import type { ReactNode } from "react";
import { CacheProvider } from "@emotion/react";
import type { EmotionCache } from "@emotion/cache";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import { createBwTheme } from "../createBwTheme";
import { createEmotionCache } from "./createEmotionCache";

export interface BwColorSchemeStorageManager {
  (options: { key: string; storageWindow?: Window | null }): {
    get(defaultValue: unknown): unknown;
    set(value: unknown): void;
    subscribe(handler: (value: unknown) => void): () => void;
  };
}

export interface BwMaterialProviderProps {
  children: ReactNode;
  theme?: Theme;
  cache?: EmotionCache;
  defaultMode?: "light" | "dark" | "system";
  storageManager?: BwColorSchemeStorageManager | null;
}

export function BwMaterialProvider({
  children,
  theme,
  cache,
  defaultMode = "system",
  storageManager,
}: BwMaterialProviderProps) {
  const fallbackCache = useRef<EmotionCache | null>(null);
  const fallbackTheme = useRef<Theme | null>(null);

  if (!cache && !fallbackCache.current) {
    fallbackCache.current = createEmotionCache();
  }
  if (!theme && !fallbackTheme.current) {
    fallbackTheme.current = createBwTheme();
  }

  const resolvedCache = cache ?? fallbackCache.current!;
  const resolvedTheme = theme ?? fallbackTheme.current!;

  return (
    <CacheProvider value={resolvedCache}>
      <ThemeProvider
        theme={resolvedTheme}
        defaultMode={defaultMode}
        storageManager={storageManager}
        modeStorageKey="mode"
        colorSchemeStorageKey="colorScheme"
        noSsr
      >
        <CssBaseline />
        {children}
      </ThemeProvider>
    </CacheProvider>
  );
}
